import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Shield, ArrowLeft, Users, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "../App";

// HTTP Status codes
const HTTP_FORBIDDEN = 403;

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      if (error.response?.status === HTTP_FORBIDDEN) {
        toast.error("Accès non autorisé");
        navigate('/admin');
      } else {
        toast.error("Erreur de chargement");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'utilisateur @${username} ? Cette action est irréversible.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      toast.success("Utilisateur supprimé");
      fetchAllUsers();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-12">
      {/* Header */}
      <header className="bg-card/80 backdrop-blur-sm border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-primary" strokeWidth={1.5} />
            <div>
              <h1 className="font-heading text-xl font-bold" data-testid="admin-users-title">Gestion des Utilisateurs</h1>
              <p className="text-sm text-muted-foreground">Tous les utilisateurs de la plateforme</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              data-testid="wishes-btn"
              onClick={() => navigate('/admin/dashboard')} 
              variant="outline" 
              size="sm"
              className="rounded-full"
            >
              <Shield className="w-4 h-4 mr-2" />
              Souhaits
            </Button>
            <Button 
              data-testid="back-btn"
              onClick={() => navigate('/')} 
              variant="ghost" 
              size="sm"
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <Card className="shadow-card">
          <CardHeader>
            <CardTitle className="font-heading" data-testid="total-users">Total: {users.length} utilisateurs</CardTitle>
          </CardHeader>
          <CardContent>
            {users.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Aucun utilisateur dans la base de données</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nom</TableHead>
                      <TableHead>Username</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Anniversaire</TableHead>
                      <TableHead>Pays</TableHead>
                      <TableHead>Ville</TableHead>
                      <TableHead>Device</TableHead>
                      <TableHead>Inscription</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.user_id} data-testid={`admin-user-${user.user_id}`}>
                        <TableCell className="font-medium">{user.name}</TableCell>
                        <TableCell className="font-mono text-sm">@{user.username}</TableCell>
                        <TableCell className="text-sm">{user.email}</TableCell>
                        <TableCell className="text-sm">{user.birthday || '-'}</TableCell>
                        <TableCell className="text-sm">{user.country || '-'}</TableCell>
                        <TableCell className="text-sm">{user.city || '-'}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                            user.last_device === 'Mobile' ? 'bg-primary/10 text-primary' :
                            user.last_device === 'Tablet' ? 'bg-accent/10 text-accent-foreground' :
                            'bg-muted text-muted-foreground'
                          }`}>
                            {user.last_device || 'Web'}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(user.created_at).toLocaleDateString('fr-FR')}
                        </TableCell>
                        <TableCell>
                          <Button
                            data-testid={`delete-user-${user.user_id}`}
                            onClick={() => handleDeleteUser(user.user_id, user.username)}
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}